// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The inspector card on the bench, in Three (M13-22).
//
// `inspector.ts` says what the card is — its rows, where it floats, where each row's rail and
// steppers are in the card's own frame — and this draws it under `MachineScene.playfield`: a
// dark face over the playfield at `cardLift`, leaning `CARD_TILT` toward the author, drawn at `1 / scale` so it is a hand
// wide in the room at any bench scale; on it, painted once per part, a label, a rail and a pair
// of steppers per row; over each rail, a knob that rides the value; and beside each label, the
// value as words, repainted only when the words change — which is every frame of a slide and
// never otherwise. The layout is `rowGeometry`'s, the same the input measures its pinches and
// taps against, so what is drawn is what is touched.
//
// The face and the steppers are one canvas the size of the card; each value is its own small
// canvas. In a build without a `document` — a test, a headless runtime — there are no canvases
// and the card is its face, its knobs and its planes, placed as they would be.

import {
  CanvasTexture,
  CircleGeometry,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three'
import {
  CARD_PADDING,
  CARD_TILT,
  DIAL_RADIUS,
  ROW_HEIGHT,
  TARGET,
  TITLE_HEIGHT,
  TITLE_WIDTH,
  cardToPlayfield,
  rowGeometry,
  titleAt,
  type InspectorView,
} from '../xr/build/inspector'
import { FINISHES } from './partsKit'

/** The face's own dark, and the ghost's pale blue for what is touched: the knob, the steppers' edge. */
const FACE_RGBA = 'rgba(12, 10, 20, 0.86)'
const INK = '#e8e6f0'
const DIM = '#8a879a'
export const KNOB_COLOR = FINISHES.ghost.color
const KNOB_CSS = '#9fd8ff'
/** The knob, room metres: a fingertip's worth of rail. */
export const KNOB_RADIUS = 0.007
/**
 * The knob a pinch is sliding (the card polish, 26 Sep 2026): lit white and a size larger, so
 * the author sees the card has the slide — and which rail — before the value has moved.
 */
export const KNOB_HELD_COLOR = 0xffffff
export const KNOB_HELD_SCALE = 1.4
/** A dial's knob is smaller than a rail's, so it sits on the rim rather than covering the dial. */
export const KNOB_DIAL_SCALE = 0.55
/** Pixels per room metre on the canvases. */
const PPM = 5120
/** A value's own plane, room metres, and its canvas. */
export const VALUE_WIDTH = 0.064
export const VALUE_HEIGHT = 0.014
const VALUE_CANVAS = { width: 320, height: 70 }
/** The title's canvas (the 1.0.113 round): eight to one, as its plane is. */
const TITLE_CANVAS = { width: 640, height: 80 }
const TITLE_INK = '#9fd8ff'
/** Where the text line sits in a row, over the rail. */
const TEXT_ABOVE = ROW_HEIGHT * 0.26
/** Lift of the planes over the face, room metres, so they are never in it. */
const OVER_FACE = 0.0008
const KNOB_OVER = 0.0016

type DocumentLike = { createElement: (tag: string) => HTMLCanvasElement }

function canvasOf(width: number, height: number): HTMLCanvasElement | undefined {
  const host = (globalThis as { document?: DocumentLike }).document
  const canvas = host?.createElement('canvas')
  if (canvas === undefined) return undefined
  canvas.width = width
  canvas.height = height
  return canvas
}

function textureOf(canvas: HTMLCanvasElement): CanvasTexture {
  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.generateMipmaps = false
  texture.minFilter = LinearFilter
  return texture
}

interface Painted {
  readonly mesh: Mesh
  readonly geometry: PlaneGeometry
  readonly material: MeshBasicMaterial
  readonly canvas: HTMLCanvasElement | undefined
  readonly texture: CanvasTexture | undefined
}

function paintedPlane(width: number, height: number, canvasWidth: number, canvasHeight: number, name: string, fallback: number): Painted {
  const canvas = canvasOf(canvasWidth, canvasHeight)
  const texture = canvas === undefined ? undefined : textureOf(canvas)
  const geometry = new PlaneGeometry(width, height)
  const material = new MeshBasicMaterial({
    ...(texture === undefined ? { color: fallback } : { map: texture }),
    transparent: true,
    opacity: texture === undefined ? 0.86 : 1,
    depthTest: false,
    depthWrite: false,
  })
  const mesh = new Mesh(geometry, material)
  mesh.name = name
  mesh.renderOrder = 1003
  mesh.frustumCulled = false
  return { mesh, geometry, material, canvas, texture }
}

function disposePainted(p: Painted): void {
  p.geometry.dispose()
  p.material.dispose()
  p.texture?.dispose()
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

/** The face: the dark, and per row the label, the rail and the steppers. Once per part. */
/** The title strip: the part's name and id, pale blue on the face's dark, fitted to its width. */
function paintTitle(title: Painted, text: string): void {
  const { canvas, texture } = title
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = FACE_RGBA
  roundedRect(ctx, 0, 0, canvas.width, canvas.height, canvas.height / 3)
  ctx.fill()
  ctx.fillStyle = TITLE_INK
  ctx.font = `600 ${Math.round(canvas.height * 0.55)}px system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, canvas.width / 2, canvas.height / 2, canvas.width * 0.92)
  texture.needsUpdate = true
}

function paintFace(face: Painted, view: InspectorView): void {
  const { canvas, texture } = face
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  const px = (x: number): number => (x + view.width / 2) * PPM
  const py = (y: number): number => (view.height / 2 - y) * PPM
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = FACE_RGBA
  roundedRect(ctx, 0, 0, canvas.width, canvas.height, CARD_PADDING * PPM)
  ctx.fill()
  view.rows.forEach((row, index) => {
    const g = rowGeometry(view, index)
    // The label, on the text line, from the card's inner edge.
    ctx.fillStyle = INK
    ctx.font = `600 ${Math.round(0.0085 * PPM)}px system-ui, sans-serif`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    // Clear of the − stepper, and squeezed rather than run into the dial or the value.
    ctx.fillText(row.label, px(g.labelLeft), py(g.y + TEXT_ABOVE), (g.labelRight - g.labelLeft) * PPM)
    // A bearing's dial (the card polish): the rim, a tick at each quarter — 0° to the right, as
    // the playfield's +x — and the centre.
    if (row.kind === 'rail' && g.dialX !== undefined) {
      ctx.strokeStyle = DIM
      ctx.lineWidth = 0.0012 * PPM
      ctx.beginPath()
      ctx.arc(px(g.dialX), py(g.railY), DIAL_RADIUS * PPM, 0, Math.PI * 2)
      ctx.stroke()
      ctx.lineWidth = 0.0008 * PPM
      for (let quarter = 0; quarter < 4; quarter++) {
        const a = (quarter * Math.PI) / 2
        ctx.beginPath()
        ctx.moveTo(px(g.dialX + DIAL_RADIUS * 0.7 * Math.cos(a)), py(g.railY + DIAL_RADIUS * 0.7 * Math.sin(a)))
        ctx.lineTo(px(g.dialX + DIAL_RADIUS * Math.cos(a)), py(g.railY + DIAL_RADIUS * Math.sin(a)))
        ctx.stroke()
      }
      ctx.fillStyle = DIM
      ctx.beginPath()
      ctx.arc(px(g.dialX), py(g.railY), 0.0012 * PPM, 0, Math.PI * 2)
      ctx.fill()
    }
    // The rail — none for a choice, which is stepped through its options, nor for a dial.
    if (row.kind === 'rail' && g.dialX === undefined) {
      ctx.strokeStyle = DIM
      ctx.lineWidth = 0.0012 * PPM
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(px(g.railLeft), py(g.railY))
      ctx.lineTo(px(g.railRight), py(g.railY))
      ctx.stroke()
      // The ends, so the rail reads as bounded.
      ctx.fillStyle = DIM
      for (const x of [g.railLeft, g.railRight]) {
        ctx.beginPath()
        ctx.arc(px(x), py(g.railY), 0.0018 * PPM, 0, Math.PI * 2)
        ctx.fill()
      }
    }
    // The steppers: a square each, TARGET across, with its glyph.
    for (const [x, glyph] of [
      [g.minusX, '−'],
      [g.plusX, '+'],
    ] as const) {
      const side = TARGET * PPM
      const left = px(x) - side / 2
      const top = py(g.y) - side / 2
      ctx.fillStyle = 'rgba(255, 255, 255, 0.07)'
      roundedRect(ctx, left, top, side, side, side * 0.18)
      ctx.fill()
      ctx.strokeStyle = KNOB_CSS
      ctx.lineWidth = 0.0006 * PPM
      ctx.stroke()
      ctx.fillStyle = INK
      ctx.font = `500 ${Math.round(0.011 * PPM)}px system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(glyph, px(x), py(g.y) + side * 0.02)
    }
  })
  texture.needsUpdate = true
}

/** A value as words, right-aligned to the rail's end. Once per change of words. */
function paintValue(plane: Painted, text: string, dim: boolean): void {
  const { canvas, texture } = plane
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = dim ? DIM : INK
  ctx.font = `600 ${Math.round(canvas.height * 0.62)}px system-ui, sans-serif`
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, canvas.width - 4, canvas.height / 2, canvas.width - 8)
  texture.needsUpdate = true
}

interface DrawnRow {
  readonly value: Painted
  readonly knob: Mesh | undefined
  text: string | undefined
}

export interface InspectorCardBuild {
  readonly group: Group
  /** This frame's view, or nothing, the scale the bench is drawn at, and the row being slid (its knob lit). */
  update(view: InspectorView | undefined, scale: number, held?: string): void
  /** The words on each row's value plane, for a test with no canvas to read. */
  readonly values: readonly string[]
  dispose(): void
}

export function buildInspectorCard(): InspectorCardBuild {
  const group = new Group()
  group.name = 'build.inspector'
  group.visible = false
  // Everything under the pivot is in room metres, in the card's frame; the pivot is at the
  // card's place in playfield metres, scaled by 1 / scale.
  const pivot = new Group()
  pivot.name = 'inspector-card'
  group.add(pivot)

  const knobGeometry = new CircleGeometry(KNOB_RADIUS, 24)
  const knobMaterial = new MeshBasicMaterial({ color: KNOB_COLOR, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
  const heldMaterial = new MeshBasicMaterial({ color: KNOB_HELD_COLOR, transparent: true, opacity: 1, depthTest: false, depthWrite: false })

  let face: Painted | undefined
  let title: Painted | undefined
  let rows: DrawnRow[] = []
  /** What the face was painted for: the part and its rows, not their values. */
  let faceKey: string | undefined

  const dropAll = (): void => {
    if (face !== undefined) {
      pivot.remove(face.mesh)
      disposePainted(face)
      face = undefined
    }
    if (title !== undefined) {
      pivot.remove(title.mesh)
      disposePainted(title)
      title = undefined
    }
    for (const row of rows) {
      pivot.remove(row.value.mesh)
      disposePainted(row.value)
      if (row.knob !== undefined) pivot.remove(row.knob)
    }
    rows = []
    faceKey = undefined
  }

  const layFace = (view: InspectorView): void => {
    // The labels are in the key (M13-26): a card in the other language is another face.
    const key = `${view.deviceId}|${view.title}|${view.rows.map((row) => `${row.path}:${row.kind}:${row.label}`).join('|')}`
    if (face !== undefined && key === faceKey) return
    dropAll()
    faceKey = key
    const width = view.width
    const height = view.height
    face = paintedPlane(width, height, Math.round(width * PPM), Math.round(height * PPM), 'inspector-face', 0x0c0a14)
    paintFace(face, view)
    pivot.add(face.mesh)
    // The title over the top edge (the 1.0.113 round): what the card is about.
    title = paintedPlane(TITLE_WIDTH, TITLE_HEIGHT, TITLE_CANVAS.width, TITLE_CANVAS.height, 'inspector-title', 0x0c0a14)
    paintTitle(title, view.title)
    title.mesh.position.set(0, titleAt(view), 0)
    pivot.add(title.mesh)
    rows = view.rows.map((row, index) => {
      const g = rowGeometry(view, index)
      const value = paintedPlane(VALUE_WIDTH, VALUE_HEIGHT, VALUE_CANVAS.width, VALUE_CANVAS.height, `inspector-value-${row.path}`, 0x000000)
      value.material.opacity = value.texture === undefined ? 0 : 1
      value.mesh.position.set(g.railRight + TARGET / 2 - VALUE_WIDTH / 2, g.y + TEXT_ABOVE, OVER_FACE)
      value.mesh.renderOrder = 1004
      pivot.add(value.mesh)
      let knob: Mesh | undefined
      if (row.kind === 'rail') {
        knob = new Mesh(knobGeometry, knobMaterial)
        knob.name = `inspector-knob-${row.path}`
        knob.renderOrder = 1005
        knob.frustumCulled = false
        knob.visible = false
        pivot.add(knob)
      }
      return { value, knob, text: undefined }
    })
  }

  let viewNow: InspectorView | undefined

  return {
    group,
    get values() {
      return rows.map((row) => row.text ?? '')
    },
    update(view, scale, held) {
      if (view === undefined) {
        if (viewNow !== undefined) dropAll()
        viewNow = undefined
        group.visible = false
        return
      }
      group.visible = true
      layFace(view)
      // Room-sized whatever the bench's scale: the card the input measures against.
      const inverse = scale > 0 ? 1 / scale : 1
      // The card's centre, through the same lean the input reads hands through.
      const [cx, cy, cz] = cardToPlayfield(view, 0, 0, 0, scale > 0 ? scale : 1)
      pivot.position.set(cx, cy, cz)
      // Leaning toward the author (the card polish): the far edge up, about the card's own x.
      pivot.rotation.set(CARD_TILT, 0, 0)
      pivot.scale.set(inverse, inverse, inverse)
      view.rows.forEach((row, index) => {
        const drawn = rows[index]
        if (drawn === undefined) return
        if (row.text !== drawn.text) {
          drawn.text = row.text
          paintValue(drawn.value, row.text, row.value === undefined)
        }
        if (drawn.knob !== undefined) {
          const g = rowGeometry(view, index)
          if (g.knobX === undefined || g.knobY === undefined) {
            drawn.knob.visible = false
          } else {
            drawn.knob.visible = true
            drawn.knob.position.set(g.knobX, g.knobY, KNOB_OVER)
            const lit = held !== undefined && held === row.path
            drawn.knob.material = lit ? heldMaterial : knobMaterial
            const size = (lit ? KNOB_HELD_SCALE : 1) * (g.dialX === undefined ? 1 : KNOB_DIAL_SCALE)
            drawn.knob.scale.set(size, size, 1)
          }
        }
      })
      viewNow = view
    },
    dispose() {
      dropAll()
      knobGeometry.dispose()
      knobMaterial.dispose()
      heldMaterial.dispose()
    },
  }
}
